import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Users, UserPlus, BookOpen, Calendar, DollarSign,
  AlertTriangle, Clock, ArrowUpRight, ArrowDownRight,
  UserCheck, TrendingUp, UserX, UserMinus, Baby
} from 'lucide-react';
import axios from 'axios';

const DashboardOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get('/dashboard/demographics');
      setData(res.data);
    } catch (err) {
      console.error('Error fetching demographics:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!data) return null;

  const { students, staff, financial, academic } = data;

  const gradeEntries = Object.entries(students.by_grade || {}).sort((a, b) => {
    const order = ['Preescolar','Kinder','1ro','2do','3ro','4to','5to','6to','7mo','8vo','9no','10mo','11mo','12vo'];
    return order.indexOf(a[0]) - order.indexOf(b[0]);
  });

  const ageEntries = Object.entries(students.age_ranges || {}).filter(([, v]) => v > 0);

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      {/* Row 1: Key Financial & Student Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Estudiantes Activos"
          value={students.total_active}
          icon={<Users className="w-5 h-5 text-white" />}
          gradient="from-blue-500 to-blue-600"
          shadow="shadow-blue-500/20"
          sub={students.new_this_month > 0 ? `+${students.new_this_month} este mes` : null}
          subPositive
        />
        <StatCard
          label="Profesores"
          value={staff.total_teachers}
          icon={<UserPlus className="w-5 h-5 text-white" />}
          gradient="from-indigo-500 to-indigo-600"
          shadow="shadow-indigo-500/20"
          sub={staff.total_employees > 0 ? `${staff.total_employees} empleados` : null}
        />
        <StatCard
          label="Ingresos del Mes"
          value={`US$ ${financial.month_revenue.toLocaleString()}`}
          icon={<DollarSign className="w-5 h-5 text-white" />}
          gradient="from-emerald-500 to-emerald-600"
          shadow="shadow-emerald-500/20"
          sub={`${financial.month_payments} pagos`}
          subPositive
        />
        <StatCard
          label="Pendiente Cobro"
          value={`US$ ${financial.pending_amount.toLocaleString()}`}
          icon={<AlertTriangle className="w-5 h-5 text-white" />}
          gradient="from-amber-500 to-amber-600"
          shadow="shadow-amber-500/20"
          sub={financial.pending_count > 0 ? `${financial.pending_count} pagos` : 'Al día'}
          subPositive={financial.pending_count === 0}
        />
      </div>

      {/* Row 2: Academic & HR */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat label="Año Académico" value={academic.active_year || 'N/D'} icon={<Calendar className="w-4 h-4 text-orange-500" />} />
        <MiniStat label="Materias" value={academic.total_subjects} icon={<BookOpen className="w-4 h-4 text-purple-500" />} />
        <MiniStat label="Ponches Hoy" value={staff.today_clockins} icon={<Clock className="w-4 h-4 text-teal-500" />} />
        <MiniStat label="Bloqueados" value={academic.active_blocks} icon={<AlertTriangle className="w-4 h-4 text-red-500" />} />
      </div>

      {/* Row 3: Demographics & Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Students by Grade */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Estudiantes por Grado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {gradeEntries.length > 0 ? gradeEntries.map(([grade, count]) => (
              <div key={grade} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-sm text-slate-600">{grade}</span>
                <span className="text-sm font-bold text-slate-800">{count}</span>
              </div>
            )) : (
              <p className="text-sm text-slate-400 text-center py-4">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* Demographics */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Demografía</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Género</p>
              <div className="grid grid-cols-3 gap-2">
                <DemoBox label="Masculino" value={students.gender.masculino} color="text-blue-600" bg="bg-blue-50" />
                <DemoBox label="Femenino" value={students.gender.femenino} color="text-pink-600" bg="bg-pink-50" />
                <DemoBox label="Otro" value={students.gender.otro} color="text-slate-600" bg="bg-slate-50" />
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Edades</p>
              {ageEntries.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {ageEntries.map(([range, count]) => (
                    <DemoBox key={range} label={range + ' años'} value={count} color="text-purple-600" bg="bg-purple-50" />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Sin datos de edad</p>
              )}
            </div>
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Estado</p>
              <div className="grid grid-cols-3 gap-2">
                <DemoBox label="Activos" value={students.total_active} color="text-green-600" bg="bg-green-50" />
                <DemoBox label="Inactivos" value={students.total_inactive} color="text-amber-600" bg="bg-amber-50" />
                <DemoBox label="Bajas" value={students.total_withdrawn} color="text-red-600" bg="bg-red-50" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial & Quick Info */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Resumen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Ingresos Recientes</p>
              {(financial.revenue_chart || []).length > 0 ? (
                <div className="space-y-1.5">
                  {financial.revenue_chart.map((r, i) => (
                    <div key={r.month || i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-sm text-slate-600">{r.month}</span>
                      <div className="text-right">
                        <span className="text-sm font-bold text-slate-800">US$ {r.revenue.toLocaleString()}</span>
                        <span className="text-[10px] text-slate-400 ml-2">({r.count} pagos)</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Sin datos</p>
              )}
            </div>
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Paquetes Más Suscritos</p>
              {financial.top_packages?.length > 0 ? (
                financial.top_packages.map((pkg, i) => (
                  <div key={pkg.name || i} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-slate-700 truncate">{pkg.name}</span>
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">{pkg.subscribers}</Badge>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400">Sin suscripciones</p>
              )}
            </div>
            {academic.upcoming_milestones?.length > 0 && (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Próximos Hitos</p>
                {academic.upcoming_milestones.map((m, i) => (
                  <div key={m.id || m.name || i} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-slate-700">{m.name}</span>
                    <span className="text-xs text-slate-400">{new Date(m.date).toLocaleDateString('es-DO')}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, gradient, shadow, sub, subPositive }) => (
  <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 p-5"
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
        <p className="text-2xl font-bold text-slate-800 truncate">{value}</p>
        {sub && (
          <p className={`text-xs mt-1 flex items-center gap-1 ${subPositive ? 'text-emerald-600' : 'text-amber-600'}`}>
            {subPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {sub}
          </p>
        )}
      </div>
      <div className={`w-11 h-11 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg ${shadow} flex-shrink-0`}>
        {icon}
      </div>
    </div>
  </div>
);

const MiniStat = ({ label, value, icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
    <div className="w-9 h-9 bg-slate-50 rounded-lg flex items-center justify-center flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-lg font-bold text-slate-800 truncate">{value}</p>
    </div>
  </div>
);

const DemoBox = ({ label, value, color, bg }) => (
  <div className={`${bg} rounded-lg p-2.5 text-center`}>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
    <p className="text-[10px] text-slate-500 leading-tight">{label}</p>
  </div>
);

export default DashboardOverview;
